// The visit's protocol record (#23) as dictated: pure rules, tested in node.
import type { IcdRef, Visit } from '@/types/api';

/** Dictated but not saved yet: no open visit yet, still being said, or the save failed. */
export type PendingRecord = { anamnesis: string[]; diagnoses: string[]; failed: boolean };

export type RecordPatch = { anamnesis?: string | null; diagnoses?: string[] };

/** `record` put back in front of what was dictated since; `failed` stops the automatic save. */
export function mergePending(
  record: PendingRecord,
  since: PendingRecord | null,
  failed: boolean
): PendingRecord {
  return {
    anamnesis: [...record.anamnesis, ...(since?.anamnesis ?? [])],
    diagnoses: [...new Set([...record.diagnoses, ...(since?.diagnoses ?? [])])],
    failed,
  };
}

/**
 * The visit's record with what was said appended: the anamnesis after the
 * existing text, the diagnoses after the existing ones. Null when nothing changes.
 */
export function recordPatch(
  visit: Pick<Visit, 'anamnesis' | 'diagnoses'>,
  said: string,
  known: readonly IcdRef[]
): RecordPatch | null {
  const patch: RecordPatch = {};
  if (said) patch.anamnesis = [visit.anamnesis, said].filter(Boolean).join(' ');
  const added = known.filter((k) => !visit.diagnoses.some((d) => d.code === k.code));
  if (added.length) patch.diagnoses = [...visit.diagnoses, ...added].map((d) => d.code);
  return patch.anamnesis || patch.diagnoses ? patch : null;
}
